import { Bell, CheckCheck, Trash2 } from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface AdminNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  tenantId?: string;
  tenantName?: string;
  organizationId?: string;
  link?: string;
  read: boolean;
  createdAt: string;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get<{ notifications: AdminNotification[] }>(
        '/platform-admin/notifications',
      );
      setNotifications(response.data.notifications ?? []);
    } catch (err) {
      setNotifications([]);
      console.error('Notifications fetch failed:', err);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
    const interval = setInterval(() => void fetchNotifications(), 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setExpandedId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMarkRead = async (id: string) => {
    try {
      await api.put(`/platform-admin/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      // Best-effort
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/platform-admin/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // Best-effort
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/platform-admin/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch {
      // Best-effort
    }
  };

  const navigateFor = (notification: AdminNotification) => {
    if (notification.link) {
      navigate(notification.link);
      return;
    }
    if (notification.organizationId) {
      navigate(`/organizations/${notification.organizationId}`);
      return;
    }
    if (notification.tenantId) {
      navigate(`/organizations?tenantId=${notification.tenantId}`);
    }
  };

  const handleSelect = (notification: AdminNotification) => {
    void handleMarkRead(notification.id);
    const hasTarget =
      !!notification.link || !!notification.organizationId || !!notification.tenantId;
    if (hasTarget) {
      setOpen(false);
      setExpandedId(null);
      navigateFor(notification);
      return;
    }
    setExpandedId((prev) => (prev === notification.id ? null : notification.id));
  };

  const formatTimeAgo = (dateString?: string): string => {
    if (!dateString) return '';
    const t = new Date(dateString).getTime();
    if (!Number.isFinite(t)) return '';
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-aia-blue"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white border rounded-lg shadow-xl z-50 max-h-[28rem] overflow-y-auto">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
            <div>
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && <p className="text-xs text-gray-500">{unreadCount} unread</p>}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                className="inline-flex items-center gap-1 text-xs text-aia-blue hover:underline"
              >
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">No notifications</div>
          ) : (
            <ul>
              {notifications.slice(0, 20).map((n) => {
                const expanded = expandedId === n.id;
                return (
                  <li key={n.id} className="border-b last:border-b-0">
                    <div
                      className={cn(
                        'px-4 py-3 hover:bg-gray-50 transition-colors',
                        !n.read && 'bg-blue-50/50',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelect(n)}
                        className="w-full text-left"
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && (
                            <span className="mt-1.5 flex-shrink-0 h-2 w-2 rounded-full bg-blue-500" />
                          )}
                          <div className={cn('flex-1 min-w-0', n.read && 'ml-4')}>
                            <p className="text-sm font-medium truncate">{n.title}</p>
                            <p
                              className={cn(
                                'text-xs text-gray-600 mt-0.5',
                                expanded ? 'whitespace-pre-wrap' : 'line-clamp-2',
                              )}
                            >
                              {n.message}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {n.tenantName && (
                                <span className="text-xs text-aia-blue">{n.tenantName}</span>
                              )}
                              <span className="text-xs text-gray-500">
                                {formatTimeAgo(n.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                      <div className="mt-2 flex items-center gap-3 pl-4">
                        {!n.read && (
                          <button
                            type="button"
                            onClick={() => void handleMarkRead(n.id)}
                            className="text-xs text-aia-blue hover:underline"
                          >
                            Mark read
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleDelete(n.id)}
                          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                          aria-label={`Delete notification ${n.title}`}
                        >
                          <Trash2 className="h-3 w-3" aria-hidden="true" />
                          Delete
                        </button>
                        {!n.link && !n.organizationId && !n.tenantId && (
                          <button
                            type="button"
                            onClick={() => setExpandedId((prev) => (prev === n.id ? null : n.id))}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            {expanded ? 'Hide detail' : 'Show detail'}
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
